import { GridColDef, GridColumnGroupingModel, GridValueFormatterParams } from "@mui/x-data-grid-premium";

export type PayrollReportKey = "review" | "real" | "reconcile" | "whenPaidByWorkDate" | "whenPaidByPayPeriod";

export type PayrollReportDefinition = {
    title: string;
    columns: GridColDef[];
    columnGroupingModel?: GridColumnGroupingModel;
    /** Numeric fields summed into the summary line above the table. */
    totals: { field: string; label: string; currency?: boolean }[];
};

export type TrainerNameLookup = (empId: number | null | undefined) => string;

// Date-only strings (yyyy-MM-dd) are parsed as local time so the displayed day doesn't shift by timezone.
export const parseDateOnly = (value: string | null | undefined): Date | null => {
    if (!value) {
        return null;
    }

    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    const parsed = match ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])) : new Date(value);

    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const formatDateOnly = (value: Date | null | undefined): string =>
    value instanceof Date
        ? value.toLocaleDateString("en-US", { year: "numeric", month: "2-digit", day: "2-digit" })
        : "";

export const toDateOnlyString = (value: Date): string => {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, "0");
    const day = String(value.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

const currencyFormatter = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });

export const formatCurrency = (value: number) => currencyFormatter.format(value);

const dateColumn = (field: string, headerName: string, width = 120): GridColDef => ({
    field,
    headerName,
    type: "date",
    width,
    valueGetter: (params) => parseDateOnly(params.row[field]),
    valueFormatter: (params: GridValueFormatterParams<Date | null>) => formatDateOnly(params.value),
});

const currencyColumn = (field: string, headerName: string, width = 110): GridColDef => ({
    field,
    headerName,
    type: "number",
    width,
    valueFormatter: (params: GridValueFormatterParams<number | null>) =>
        params.value == null ? "" : formatCurrency(Number(params.value)),
});

const numberColumn = (field: string, headerName: string, width = 90): GridColDef => ({
    field,
    headerName,
    type: "number",
    width,
});

const trainerColumn = (lookup: TrainerNameLookup, headerName: string, width = 200): GridColDef => ({
    field: "trainerName",
    headerName,
    width,
    valueGetter: (params) => lookup(params.row.eventTrainer),
});

// Fixed values the legacy "PayRoll Report - for Finance" stamped onto every line for the payroll import.
const FINANCE_JOB_CODE = 131170;
const FINANCE_ORG1_BUSINESS_UNIT = "GLMKTS";
const FINANCE_ORG3_DEPT = "055";

// GL codes the legacy "When Was I Paid?" reports break out into their own Fee/Hours columns.
const WHEN_PAID_GL_CODES = ["6002", "6005", "6006", "6008", "6009"];

const whenPaidColumns = (lookup: TrainerNameLookup): GridColDef[] => {
    const glColumns = WHEN_PAID_GL_CODES.flatMap((code) => [
        currencyColumn(`fee${code}`, "Fee"),
        numberColumn(`hours${code}`, "Hours"),
    ]);

    return [
        trainerColumn(lookup, "Trainer"),
        { field: "eventName", headerName: "Event", width: 260 },
        { field: "eventCode", headerName: "Event Code", width: 150 },
        dateColumn("workDate", "Work Date"),
        dateColumn("payDate", "Pay Date"),
        {
            ...dateColumn("payDay", "Paid On"),
            // A line with no finance pay day scheduled yet hasn't been (or can't yet be) paid.
            valueFormatter: (params: GridValueFormatterParams<Date | null>) =>
                params.value ? formatDateOnly(params.value) : "Not scheduled",
        },
        ...glColumns,
        currencyColumn("feeOther", "Fee"),
        numberColumn("hoursOther", "Hours"),
        {
            ...currencyColumn("fee", "Fee", 120),
            headerName: "Total Fee",
        },
        {
            ...numberColumn("hours", "Hours", 120),
            headerName: "Total Hours",
        },
    ];
};

const whenPaidColumnGroups: GridColumnGroupingModel = [
    ...WHEN_PAID_GL_CODES.map((code) => ({
        groupId: `gl${code}`,
        headerName: code,
        headerAlign: "center" as const,
        children: [{ field: `fee${code}` }, { field: `hours${code}` }],
    })),
    {
        groupId: "glOther",
        headerName: "Other",
        headerAlign: "center",
        children: [{ field: "feeOther" }, { field: "hoursOther" }],
    },
];

const whenPaidTotals: PayrollReportDefinition["totals"] = [
    { field: "hours", label: "Total Hours" },
    { field: "fee", label: "Total Fee", currency: true },
];

export const getReportDefinition = (key: PayrollReportKey, lookup: TrainerNameLookup): PayrollReportDefinition => {
    switch (key) {
        case "review":
            return {
                title: "Review Payroll Report",
                columns: [
                    { field: "adminLoc", headerName: "Admin Loc", width: 100 },
                    { field: "shortEventCode", headerName: "Event Code", width: 150 },
                    { field: "lob", headerName: "LOB", width: 80 },
                    { field: "eventName", headerName: "Event", width: 260 },
                    dateColumn("payPeriodEndDate", "Charge Date"),
                    trainerColumn(lookup, "Trainer"),
                    numberColumn("units", "Units"),
                    numberColumn("hours", "Hours"),
                    { field: "rateCode", headerName: "Pay Rate", width: 100 },
                    currencyColumn("fee", "Fee"),
                    {
                        ...currencyColumn("calculatedFee", "Calculated Fee", 130),
                        valueGetter: (params) => Number(params.row.units ?? 0) * Number(params.row.currentFee ?? 0),
                    },
                    dateColumn("workDate", "Work Date"),
                    dateColumn("revRecDate", "Rev Rec Date"),
                    { field: "glLetters", headerName: "GL Letters", width: 100 },
                ],
                totals: [
                    { field: "hours", label: "Total Hours" },
                    { field: "fee", label: "Total Fee", currency: true },
                ],
            };
        case "real":
            return {
                title: "Real Payroll Report",
                columns: [
                    dateColumn("payPeriodEndDate", "Charge Date"),
                    dateColumn("revRecDate", "Rev Rec Date"),
                    trainerColumn(lookup, "Adjunct Name"),
                    { field: "eventTrainer", headerName: "Employee ID", width: 110 },
                    { field: "shortEventCode", headerName: "Event Code", width: 150 },
                    { field: "lob", headerName: "Group Number", width: 120 },
                    { field: "adminLoc", headerName: "Org4 GLLoc", width: 110 },
                    { field: "glGroup", headerName: "Org2 Group", width: 110 },
                    numberColumn("units", "# Hours"),
                    numberColumn("hours", "Real Hours"),
                    { field: "rateCode", headerName: "Earnings Code", width: 120 },
                    currencyColumn("fee", "Fee"),
                    { field: "amount", headerName: "Amount", width: 100, valueGetter: () => "" },
                    { field: "pieceCode", headerName: "Piece Code", width: 100, valueGetter: () => "" },
                    { field: "shiftCode", headerName: "Shift Code", width: 100, valueGetter: () => "" },
                    { field: "location", headerName: "Location", width: 100, valueGetter: () => "" },
                    { field: "jobCode", headerName: "Job Code", width: 100, valueGetter: () => FINANCE_JOB_CODE },
                    {
                        field: "org1BusinessUnit",
                        headerName: "Org1 Business Unit",
                        width: 150,
                        valueGetter: () => FINANCE_ORG1_BUSINESS_UNIT,
                    },
                    { field: "org3Dept", headerName: "Org3 Dept", width: 100, valueGetter: () => FINANCE_ORG3_DEPT },
                    { field: "glLetters", headerName: "GL Letters", width: 100 },
                ],
                totals: [
                    { field: "hours", label: "Total Hours" },
                    { field: "fee", label: "Total Fee", currency: true },
                ],
            };
        case "reconcile":
            return {
                title: "Reconcile Payroll Report",
                columns: [
                    trainerColumn(lookup, "Trainer"),
                    { field: "shortEventCode", headerName: "Event Code", width: 150 },
                    { field: "adminLoc", headerName: "Admin Loc", width: 100 },
                    numberColumn("units", "Units"),
                    numberColumn("hours", "Hours"),
                    { field: "rateCode", headerName: "Rate Code", width: 100 },
                    currencyColumn("fee", "Fee"),
                    { field: "eventTrainer", headerName: "Employee ID", width: 110 },
                ],
                totals: [
                    { field: "hours", label: "Total Hours" },
                    { field: "fee", label: "Total Fee", currency: true },
                ],
            };
        case "whenPaidByWorkDate":
            return {
                title: "When Was I Paid? - By Date Work Done",
                columns: whenPaidColumns(lookup),
                columnGroupingModel: whenPaidColumnGroups,
                totals: whenPaidTotals,
            };
        case "whenPaidByPayPeriod":
            return {
                title: "When Was I Paid? - By Date of Pay Period",
                columns: whenPaidColumns(lookup),
                columnGroupingModel: whenPaidColumnGroups,
                totals: whenPaidTotals,
            };
    }
};
